'use client';

import clsx from 'clsx';
import { memo, useEffect, useMemo, useState } from 'react';
import type { IGame } from '~/types/api';
import { getLocalDateTime } from '~/utils/getLocalDateTime';
import { ScheduleGame } from './ScheduleGame';

type ScheduleGamesProps = {
  games: IGame[];
  className?: string;
  isAllStarCup?: boolean;
  initialGameCount: number;
  showLoadMore?: boolean;
  isVenue?: boolean;
};

export function ScheduleList({
  games,
  className,
  showLoadMore = true,
  initialGameCount,
  isAllStarCup = false,
  isVenue = false,
}: ScheduleGamesProps) {
  // State to track if component has mounted (client-side)
  const [isMounted, setIsMounted] = useState(false);

  // Update on client mount to prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Group doubleheader games that occur on the same day
  const processedGames = useMemo(() => {
    // Create a map to group games by date
    const gamesByDate = new Map();

    // Sort games by date first before slicing
    const sortedGames = [...games].sort((a, b) => {
      const dateA = a.gameDate || '';
      const dateB = b.gameDate || '';
      return dateA.localeCompare(dateB);
    });

    // First pass: organize games by date
    sortedGames.slice(0, initialGameCount).forEach((game) => {
      // Use local time for date grouping instead of server time
      let dateKey: string | undefined;

      if (game.gameDateIso && isMounted) {
        // Use the ISO date and convert to local time for grouping (only after mount)
        const localTime = getLocalDateTime(game.gameDateIso);
        if (localTime && typeof localTime !== 'string') {
          // Extract just the date part from the local time string
          // The localTime.date is already formatted like "Jul 11, 2024"
          // Parse it to get a proper date key in YYYY-MM-DD format
          const localDate = new Date(localTime.date);
          // Use local date methods instead of toISOString() to avoid UTC conversion
          const year = localDate.getFullYear();
          const month = String(localDate.getMonth() + 1).padStart(2, '0');
          const day = String(localDate.getDate()).padStart(2, '0');
          dateKey = `${year}-${month}-${day}`;
        }
      } else if (game.gameDate) {
        // Fallback to gameDate if gameDateIso is not available or not mounted yet
        dateKey = game.gameDate.split('T')[0];
      }

      // Make sure we have a dateKey, even for makeup games
      if (!dateKey && game.cancelledGame) {
        // If no date but it's a makeup game, use the cancelled game's date
        if (game.cancelledGame.gameDateIso && isMounted) {
          const localTime = getLocalDateTime(game.cancelledGame.gameDateIso);
          if (localTime && typeof localTime !== 'string') {
            const localDate = new Date(localTime.date);
            const year = localDate.getFullYear();
            const month = String(localDate.getMonth() + 1).padStart(2, '0');
            const day = String(localDate.getDate()).padStart(2, '0');
            dateKey = `${year}-${month}-${day}`;
          }
        } else if (game.cancelledGame.gameDate) {
          dateKey = game.cancelledGame.gameDate.split('T')[0];
        }

        if (dateKey) {
          if (!gamesByDate.has(dateKey)) {
            gamesByDate.set(dateKey, []);
          }
          gamesByDate.get(dateKey).push(game);
        }
        return; // Skip the rest of this iteration
      }

      if (dateKey) {
        if (!gamesByDate.has(dateKey)) {
          gamesByDate.set(dateKey, []);
        }
        gamesByDate.get(dateKey).push(game);
      }
    });

    // Second pass: process each date's games to group doubleheaders
    const result = [];

    // Convert Map entries to array and sort by date
    const sortedDates = Array.from(gamesByDate.entries()).sort(
      ([dateA], [dateB]) => dateA.localeCompare(dateB),
    );

    for (const [_, dateGames] of sortedDates) {
      // Sort games by time for consistent ordering
      dateGames.sort(
        (a: IGame, b: IGame) =>
          a.gameDate?.localeCompare(b.gameDate ?? '') ?? 0,
      );

      // Process games for this date
      let i = 0;
      while (i < dateGames.length) {
        const currentGame = dateGames[i];

        // Check if this game is a makeup game
        if (currentGame.cancelledGame !== null) {
          // Add the makeup game as a single game
          result.push(currentGame);
          i += 1;
        }
        // Check if this game and the next one form a doubleheader
        else if (
          i + 1 < dateGames.length &&
          (currentGame.doubleHeaderFlg || dateGames[i + 1].doubleHeaderFlg)
        ) {
          // Create a group of doubleheader games
          const doubleHeaderGroup = [currentGame, dateGames[i + 1]];
          result.push(doubleHeaderGroup);
          i += 2; // Skip both games
        } else {
          // Regular single game
          result.push(currentGame);
          i += 1;
        }
      }
    }

    return result;
  }, [games, initialGameCount, isMounted]);

  // Memoize the Game component
  const MemoizedGame = memo(ScheduleGame);

  return (
    <div className={clsx('flex flex-col gap-y-6', className)}>
      {!!processedGames.length && (
        <div className="mb-6 border-current last:mb-0 lg:rounded lg:border">
          <ul className="flex flex-col gap-y-4 md:gap-y-8 lg:gap-y-0">
            {processedGames.map((item, i) => {
              return (
                <MemoizedGame
                  key={`game-${i}`}
                  item={item}
                  isLast={i === processedGames.length - 1}
                  isAllStarCup={isAllStarCup}
                  isVenue={isVenue}
                />
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
